import {OverviewSection, TabLayout} from '@/layout/TabLayout';
import {TopBar} from '@/layout/Page/TopBar';
import {EditableField} from '@/components/styled/EditableField';
import {ListItemBadge} from '@/components/styled/ListItem/ListItem';
import {
  GetRulesDocument,
  useCreateRuleMutation,
  useRuleCommunitiesQuery,
} from '@/generated/graphql';
import {RuleSettingsIcon} from '@/material-symbols';
import {edgesToNodes} from '@ark/react';
import {Box} from '@mui/material';
import {ActionButton} from '@ark/web';
import {useSnackbar} from 'notistack';
import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {NewRuleOverviewTab} from './NewRuleOverviewTab';

type TabValue = 'overview' | 'conditions' | 'actions';

export const NewRulePage = () => {
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const [currentTab, setCurrentTab] = useState<TabValue>('overview');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCommunityIds, setSelectedCommunityIds] = useState<string[]>(
    [],
  );
  const [isCreating, setIsCreating] = useState(false);
  const [createRule] = useCreateRuleMutation();

  const {data: communitiesData, loading: communitiesLoading} =
    useRuleCommunitiesQuery({
      variables: {orderBy: 'name'},
    });

  const allCommunities = useMemo(() => {
    return edgesToNodes(communitiesData?.communities?.edges) || [];
  }, [communitiesData?.communities?.edges]);

  const handleCommunityToggle = (communityId: string, checked: boolean) => {
    setSelectedCommunityIds(prev =>
      checked ? [...prev, communityId] : prev.filter(id => id !== communityId),
    );
  };

  const canCreateRule =
    name.trim().length > 0 && selectedCommunityIds.length > 0;

  const handleCreateRule = async () => {
    if (!canCreateRule) return;

    setIsCreating(true);
    try {
      const response = await createRule({
        variables: {
          input: {
            name: name.trim(),
            description: description || null,
            communities: selectedCommunityIds,
          },
        },
        refetchQueries: [GetRulesDocument],
      });

      const errors = response.data?.createRule?.errors;
      if (errors?.length && errors[0]) {
        enqueueSnackbar(errors[0].messages[0], {variant: 'error'});
        setIsCreating(false);
        return;
      }

      const newRuleId = response.data?.createRule?.rule?.id;
      if (newRuleId) {
        enqueueSnackbar('Rule created successfully', {variant: 'success'});
        navigate(`/rules/${newRuleId}`);
      }
    } catch {
      enqueueSnackbar('Failed to create rule. Please try again.', {
        variant: 'error',
      });
      setIsCreating(false);
    }
  };

  const tabs = [
    {
      label: 'Overview',
      value: 'overview',
      content: (
        <NewRuleOverviewTab
          allCommunities={allCommunities}
          selectedCommunityIds={selectedCommunityIds}
          onCommunityToggle={handleCommunityToggle}
          loading={communitiesLoading}
        />
      ),
    },
    {
      label: 'Conditions',
      value: 'conditions',
      content: <Box />,
      disabled: true,
    },
    {
      label: 'Actions',
      value: 'actions',
      content: <Box />,
      disabled: true,
    },
  ];

  return (
    <Box>
      <TopBar
        title="New Rule"
        breadcrumbs={[{label: 'Rules', path: '/rules'}, {label: 'New Rule'}]}
        sx={{
          borderBottom: theme => `1px solid ${theme.palette.background.paper}`,
        }}
        Actions={[
          <ActionButton
            key="create-rule"
            variant="primary"
            sx={{height: 'fit-content'}}
            onClick={handleCreateRule}
            disabled={!canCreateRule || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Rule'}
          </ActionButton>,
        ]}
      />
      <TabLayout
        tabs={tabs}
        value={currentTab}
        onChange={newValue => setCurrentTab(newValue as TabValue)}
        defaultTab="overview"
        headerIcon={
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RuleSettingsIcon
                style={{fontSize: 24, color: 'text.secondary'}}
              />
            </Box>
            <ListItemBadge color="warning">Pending</ListItemBadge>
          </Box>
        }
        header={
          <OverviewSection>
            <EditableField
              label="Rule Name"
              value={name}
              originalValue=""
              onChange={setName}
              onSave={async () => {}}
              variant="title"
              maxLength={100}
              placeholder="Enter rule name..."
            />
            <EditableField
              label="Description"
              value={description}
              originalValue=""
              onChange={setDescription}
              onSave={async () => {}}
              maxLength={500}
              multiline
              placeholder="Enter description (optional)..."
            />
          </OverviewSection>
        }
      />
    </Box>
  );
};
